import {
    HIGHLIGHT_CLASS,
    highlightCard,
} from 'components/WorkspaceEditor/components/ContentEditor/contentUtils'
import { ScrollToContent } from 'components/WorkspaceEditor/components/TrackChangesPanel/services/ScrollToTreeNode/store/scrollStore'
import { isModifiedOrReplaced } from 'components/WorkspaceEditor/components/TrackChangesPanel/utils/TrackChangesAndSuggestionsUtils'
import {
    TrackChangeStatus,
    TrackChangeType,
    TrackedChange,
} from 'models/changes'
import { useCallback, useMemo, useRef } from 'react'
import { useIsTabDifferent } from './useIsTabDifferent'

/**
 * Custom hook to handle click events on track change cards.
 * Highlights the card and sets the scroll position to the corresponding content.
 *
 * @param cardRef - Reference to the card element.
 * @param cardSuggestionId - ID of the card suggestion.
 * @param isNotDefaultSuggestionCardFilter - Flag indicating if the card is not a default suggestion filter.
 * @param trackChanges - The tracked change associated with the card.
 * @param setScrollToContent - Function to set the scroll position to the content.
 * @return A callback function to handle the click event.
 */
export const useTrackChangeCardClickHandler = (
    cardRef: React.RefObject<HTMLDivElement>,
    cardSuggestionId: string | undefined,
    isNotDefaultSuggestionCardFilter: boolean,
    trackChanges?: TrackedChange | undefined,
    setScrollToContent?: (content: ScrollToContent) => void,
    setIsHandlingChangeTab?: (isHandling: boolean) => void
) => {
    const isTabDifferent = useIsTabDifferent(trackChanges)
    const setIsHandlingChangeTabRef = useRef(setIsHandlingChangeTab)
    setIsHandlingChangeTabRef.current = setIsHandlingChangeTab
    const setScrollToContentRef = useRef(setScrollToContent)
    setScrollToContentRef.current = setScrollToContent

    const trackChangesRef = useRef(trackChanges)
    trackChangesRef.current = trackChanges

    const isRejectedAddedStatus = useMemo(
        () =>
            trackChanges?.status === TrackChangeStatus.REJECTED &&
            trackChanges?.changeType === TrackChangeType.ADDED,
        [trackChanges?.status]
    )
    const isAcceptedDeletedStatus = useMemo(
        () =>
            trackChanges?.status === TrackChangeStatus.APPROVED &&
            trackChanges?.changeType === TrackChangeType.DELETED,
        [trackChanges?.status]
    )

    return useCallback(() => {
        const cardElement = cardRef.current
        const shouldHighlightCard =
            cardElement && !cardElement.classList.contains(HIGHLIGHT_CLASS)
        const contentId = isModifiedOrReplaced(trackChangesRef.current)
            ? trackChangesRef.current?.contentItemElementId
            : cardSuggestionId
        const cardId = trackChangesRef.current?.id
        if (shouldHighlightCard) {
            highlightCard(
                cardId,
                HIGHLIGHT_CLASS,
                isNotDefaultSuggestionCardFilter
            )
        }

        if (isRejectedAddedStatus || isAcceptedDeletedStatus) {
            return
        }
        setIsHandlingChangeTab?.(isTabDifferent)

        setScrollToContentRef.current?.({
            id: contentId ?? null,
            correspondingTrackChange: trackChangesRef.current ?? null,
        })
    }, [
        cardSuggestionId,
        isNotDefaultSuggestionCardFilter,
        isTabDifferent,
        isRejectedAddedStatus,
        isAcceptedDeletedStatus,
    ])
}
